/**
 * Sport configuration types and definitions for multi-sport support.
 *
 * KAN-11809: the runtime "which sports are enabled" list now lives in
 * `public.sport_config.is_active` and is read via the `useAvailableSportConfigs`
 * hook in `hooks/useSports.ts`. The static `SPORT_CONFIGS` map below is
 * the metadata source (period structure, score format, display rules) —
 * intersect it with the DB list at runtime, not at build time.
 */

export type SportId = 'ncaaf' | 'nfl' | 'ncaab' | 'nba' | 'mlb' | 'nhl';
export type SportIdOrNull = SportId | null;

export interface SportFeatures {
  hasDowns?: boolean;
  hasFieldPosition?: boolean;
  hasPossession?: boolean;
  hasShootingStats?: boolean;
  hasPitchingStats?: boolean;
  hasShots?: boolean;
  hasPowerPlay?: boolean;
  hasRedZone?: boolean;
  hasBonus?: boolean;
  hasShotClock?: boolean;
}

export interface SportDisplay {
  periodLabel: (period: number, isOvertime?: boolean) => string;
  scoreFormat: 'points' | 'goals' | 'runs';
  timeFormat: 'minutes_seconds' | 'decimal' | 'innings' | 'none';
  possessionIndicator?: 'football' | 'basketball' | 'none';
}

export interface SportApiConfig {
  oddsApiSport: string;
  statsEndpoint?: string;
  dataProvider?: string;
}

export interface SportConfig {
  id: SportId;
  name: string;
  shortName: string;

  // Period/timing configuration
  periodType: 'quarter' | 'half' | 'period' | 'inning';
  periodCount: number;
  periodLength: number; // in minutes
  hasOvertime: boolean;
  overtimeFormat: 'sudden_death' | 'timed' | 'shootout' | 'extra_innings' | 'none';

  // Sport-specific features
  features: SportFeatures;

  // Display configuration
  display: SportDisplay;

  // API configuration
  api: SportApiConfig;

  // Season structure
  seasonStructure: {
    hasWeeks: boolean;
    hasPlayoffs: boolean;
    hasConferences: boolean;
    hasDivisions: boolean;
  };
}

/**
 * Sport-specific data interfaces
 */
export interface FootballData {
  quarter: number;
  down?: number;
  distance?: number;
  fieldPosition?: number;
  possession?: string;
  yardLine?: string;
  situation?: string;
  isRedZone?: boolean;
  timeouts?: {
    home: number;
    away: number;
  };
}

export interface BasketballData {
  period: number;
  shotClock?: number;
  bonus?: {
    home: boolean;
    away: boolean;
  };
  possession?: string;
  fouls?: {
    home: number;
    away: number;
  };
  timeouts?: {
    home: number;
    away: number;
  };
}

export interface BaseballData {
  inning: number;
  isTop: boolean;
  outs: number;
  balls: number;
  strikes: number;
  onBase?: {
    first?: boolean;
    second?: boolean;
    third?: boolean;
  };
  currentBatter?: string;
  currentPitcher?: string;
}

export interface HockeyData {
  period: number;
  powerPlay?: {
    team?: string;
    timeRemaining?: string;
  };
  penalties?: Array<{
    team: string;
    player: string;
    time: string;
    duration: number;
  }>;
  shots?: {
    home: number;
    away: number;
  };
}

export type SportSpecificData = FootballData | BasketballData | BaseballData | HockeyData;

/**
 * Sport configurations
 */
export const SPORT_CONFIGS: Record<SportId, SportConfig> = {
  ncaaf: {
    id: 'ncaaf',
    name: 'College Football',
    shortName: 'NCAAF',
    periodType: 'quarter',
    periodCount: 4,
    periodLength: 15,
    hasOvertime: true,
    overtimeFormat: 'sudden_death',
    features: {
      hasDowns: true,
      hasFieldPosition: true,
      hasPossession: true,
      hasRedZone: true,
    },
    display: {
      periodLabel: (period: number, isOvertime?: boolean) => {
        if (isOvertime) return `OT${period > 4 ? period - 4 : ''}`;
        return `Q${period}`;
      },
      scoreFormat: 'points',
      timeFormat: 'minutes_seconds',
      possessionIndicator: 'football',
    },
    api: {
      oddsApiSport: 'americanfootball_ncaaf',
      dataProvider: 'cfbd',
    },
    seasonStructure: {
      hasWeeks: true,
      hasPlayoffs: true,
      hasConferences: true,
      hasDivisions: false,
    },
  },

  nfl: {
    id: 'nfl',
    name: 'NFL',
    shortName: 'NFL',
    periodType: 'quarter',
    periodCount: 4,
    periodLength: 15,
    hasOvertime: true,
    overtimeFormat: 'sudden_death',
    features: {
      hasDowns: true,
      hasFieldPosition: true,
      hasPossession: true,
      hasRedZone: true,
    },
    display: {
      periodLabel: (period: number, isOvertime?: boolean) => {
        if (isOvertime) return 'OT';
        return `Q${period}`;
      },
      scoreFormat: 'points',
      timeFormat: 'minutes_seconds',
      possessionIndicator: 'football',
    },
    api: {
      oddsApiSport: 'americanfootball_nfl',
    },
    seasonStructure: {
      hasWeeks: true,
      hasPlayoffs: true,
      hasConferences: true,
      hasDivisions: true,
    },
  },

  ncaab: {
    id: 'ncaab',
    name: 'College Basketball',
    shortName: 'NCAAB',
    periodType: 'half',
    periodCount: 2,
    periodLength: 20,
    hasOvertime: true,
    overtimeFormat: 'timed',
    features: {
      hasPossession: true,
      hasShootingStats: true,
      hasBonus: true,
      hasShotClock: true,
    },
    display: {
      periodLabel: (period: number, isOvertime?: boolean) => {
        if (isOvertime) return `OT${period > 2 ? period - 2 : ''}`;
        return `${period}H`;
      },
      scoreFormat: 'points',
      timeFormat: 'minutes_seconds',
      possessionIndicator: 'basketball',
    },
    api: {
      oddsApiSport: 'basketball_ncaab',
    },
    seasonStructure: {
      hasWeeks: false,
      hasPlayoffs: true,
      hasConferences: true,
      hasDivisions: false,
    },
  },

  nba: {
    id: 'nba',
    name: 'NBA',
    shortName: 'NBA',
    periodType: 'quarter',
    periodCount: 4,
    periodLength: 12,
    hasOvertime: true,
    overtimeFormat: 'timed',
    features: {
      hasPossession: true,
      hasShootingStats: true,
      hasBonus: true,
      hasShotClock: true,
    },
    display: {
      periodLabel: (period: number, isOvertime?: boolean) => {
        if (isOvertime) return `OT${period > 4 ? period - 4 : ''}`;
        return `Q${period}`;
      },
      scoreFormat: 'points',
      timeFormat: 'minutes_seconds',
      possessionIndicator: 'basketball',
    },
    api: {
      oddsApiSport: 'basketball_nba',
    },
    seasonStructure: {
      hasWeeks: false,
      hasPlayoffs: true,
      hasConferences: true,
      hasDivisions: true,
    },
  },

  mlb: {
    id: 'mlb',
    name: 'MLB',
    shortName: 'MLB',
    periodType: 'inning',
    periodCount: 9,
    periodLength: 0, // No fixed time
    hasOvertime: true,
    overtimeFormat: 'extra_innings',
    features: {
      hasPitchingStats: true,
    },
    display: {
      periodLabel: (period: number) => {
        const suffix = period === 1 ? 'st' : period === 2 ? 'nd' : period === 3 ? 'rd' : 'th';
        return `${period}${suffix}`;
      },
      scoreFormat: 'runs',
      timeFormat: 'innings',
      possessionIndicator: 'none',
    },
    api: {
      oddsApiSport: 'baseball_mlb',
    },
    seasonStructure: {
      hasWeeks: false,
      hasPlayoffs: true,
      hasConferences: false,
      hasDivisions: true,
    },
  },

  nhl: {
    id: 'nhl',
    name: 'NHL',
    shortName: 'NHL',
    periodType: 'period',
    periodCount: 3,
    periodLength: 20,
    hasOvertime: true,
    overtimeFormat: 'sudden_death',
    features: {
      hasShots: true,
      hasPowerPlay: true,
    },
    display: {
      periodLabel: (period: number, isOvertime?: boolean) => {
        if (isOvertime) return 'OT';
        return `P${period}`;
      },
      scoreFormat: 'goals',
      timeFormat: 'minutes_seconds',
      possessionIndicator: 'none',
    },
    api: {
      oddsApiSport: 'icehockey_nhl',
    },
    seasonStructure: {
      hasWeeks: false,
      hasPlayoffs: true,
      hasConferences: true,
      hasDivisions: true,
    },
  },
};

/**
 * Helper functions
 */
export const getSportConfig = (sportId: SportId): SportConfig => {
  return SPORT_CONFIGS[sportId] || SPORT_CONFIGS.ncaaf;
};

export const getSportByOddsApiName = (oddsApiSport: string): SportConfig | undefined => {
  return Object.values(SPORT_CONFIGS).find(config => config.api.oddsApiSport === oddsApiSport);
};

/**
 * Type guards
 */
export const isFootballData = (data: SportSpecificData): data is FootballData => {
  return 'quarter' in data || 'down' in data;
};

export const isBasketballData = (data: SportSpecificData): data is BasketballData => {
  return 'period' in data && 'shotClock' in data;
};

export const isBaseballData = (data: SportSpecificData): data is BaseballData => {
  return 'inning' in data && 'outs' in data;
};

export const isHockeyData = (data: SportSpecificData): data is HockeyData => {
  return 'period' in data && 'powerPlay' in data;
};